import { useEffect, useState } from 'react';
import { Printer, Percent, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import MoneyInput from '@/components/MoneyInput';
import { rupiah } from '@/lib/format';
import { draftReceiptText, whatsappReceiptUrl, whatsappMonospace } from '@/lib/draft-receipt.mjs';

function WhatsAppIcon({ className }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20.5 11.6a8.5 8.5 0 0 1-12.7 7.4L3 20.5l1.4-4.7A8.5 8.5 0 1 1 20.5 11.6Z" />
    <path d="m8.5 7.2 1.3 2.4-1 1.1a9 9 0 0 0 4.4 4.1l1-1.2 2.5 1.2c.1 1.4-1.1 2.1-2.4 1.8-3.7-.9-6.5-3.6-7.3-6.7-.3-1.2.3-2.6 1.5-2.7Z" />
  </svg>;
}

const actions = [
  { id: 'print', label: 'Cetak Resi', title: 'Cetak Resi (F9)', shortcut: 'F9', icon: Printer, color: 'text-brand-readable', testid: 'print-draft-btn' },
  { id: 'whatsapp', label: 'WhatsApp', title: 'Resi WhatsApp (F10)', shortcut: 'F10', icon: WhatsAppIcon, color: 'text-success-readable', testid: 'cart-whatsapp-btn' },
  { id: 'discount', label: 'Diskon', title: 'Diskon transaksi (F6)', shortcut: 'F6', icon: Percent, color: 'text-orange-600 dark:text-orange-400', testid: 'cart-discount-btn' },
  { id: 'note', label: 'Catatan', title: 'Tulis catatan resi (F7)', shortcut: 'F7', icon: StickyNote, color: 'text-violet-600 dark:text-violet-400', testid: 'cart-note-btn' },
];
const actionDisabled = (id, readOnly, itemCount, settlementTotal) => readOnly || (!itemCount && !settlementTotal) || (id === 'discount' && !itemCount);

export default function CartActions({ action, onAction, tx, storeName, phone, readOnly, onDiscount, onNote, onPrint, onCloseFocus }) {
  useEffect(() => {
    const onKey = event => {
      if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return;
      const entry = actions.find(item => item.shortcut === event.key);
      if (!entry) return;
      event.preventDefault();
      if (event.repeat || actionDisabled(entry.id, readOnly, tx.items.length, tx.settlement_total)
        || document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (entry.id === 'print') onPrint();
      else onAction(entry.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onAction, onPrint, readOnly, tx.items.length, tx.settlement_total]);

  return <>
    <div className="grid grid-cols-4 gap-2" data-testid="cart-actions">
      {actions.map(({ id, label, title, shortcut, icon: Icon, color, testid }) => <Button key={id} data-testid={testid} title={title} aria-label={title} aria-keyshortcuts={shortcut}
        variant="outline" disabled={actionDisabled(id, readOnly, tx.items.length, tx.settlement_total)} onClick={() => id === 'print' ? onPrint() : onAction(id)}
        className={`h-14 min-w-0 flex-col gap-1 px-1 border-ui-line bg-ui-card text-ui-text hover:bg-ui-soft ${((id === 'discount' && tx.discount > 0) || (id === 'note' && tx.note)) ? 'border-brand text-brand-readable' : ''}`}>
        <span className="flex items-center gap-1"><Icon className={`h-5 w-5 ${color}`} /><kbd className="rounded border border-ui-line px-1 text-[9px] font-mono leading-4 text-ui-muted">{shortcut}</kbd></span><span className="text-[10px] sm:text-xs">{label}</span>
      </Button>)}
    </div>
    {action && <ActionDialog key={action} action={action} onClose={() => onAction(null)} tx={tx} storeName={storeName} phone={phone}
      onDiscount={onDiscount} onNote={onNote} onCloseFocus={onCloseFocus} />}
  </>;
}

function ActionDialog({ action, onClose, tx, storeName, phone, onDiscount, onNote, onCloseFocus }) {
  const [value, setValue] = useState(action === 'discount' ? tx.discount : action === 'note' ? tx.note || '' : phone || '');
  const message = draftReceiptText(tx, storeName);
  const whatsappUrl = whatsappReceiptUrl(value, whatsappMonospace(message));
  const discountInvalid = action === 'discount' && (!Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > tx.subtotal);
  const save = event => {
    event.preventDefault();
    if (action === 'discount' && !discountInvalid) { onDiscount(Number(value) || 0); onClose(); }
    if (action === 'note') { onNote(value.trim()); onClose(); }
  };
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent data-testid="cart-action-dialog" className="bg-ui-card border-ui-line text-ui-text w-[calc(100%-2rem)] max-w-sm max-h-[90vh] overflow-y-auto"
      onCloseAutoFocus={event => { event.preventDefault(); onCloseFocus(); }}>
      <DialogHeader>
        <DialogTitle>{actions.find(item => item.id === action)?.title}</DialogTitle>
        <DialogDescription>{action === 'discount' ? 'Potongan rupiah untuk seluruh transaksi.' : action === 'note' ? 'Catatan ini akan tampil pada resi pesanan dan struk pembayaran.' : action === 'whatsapp' ? 'Periksa nomor dan isi resi, lalu lanjutkan ke WhatsApp.' : 'Periksa resi pesanan sebelum dicetak.'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="space-y-4">
        {action === 'discount' && <div className="space-y-2">
          <label htmlFor="receipt-discount" className="text-sm">Diskon (Rp)</label>
          <MoneyInput id="receipt-discount" data-testid="discount-input" value={value} onValueChange={setValue} className="h-11 bg-ui-soft border-ui-line" />
          <p className="text-sm text-ui-muted">Subtotal {rupiah(tx.subtotal)}</p>
          {discountInvalid ? <p role="alert" className="text-sm text-danger-readable">Diskon harus antara 0 dan {rupiah(tx.subtotal)}.</p> : <p className="font-semibold">Total {rupiah(tx.subtotal - Number(value || 0))}</p>}
        </div>}
        {action === 'note' && <div className="space-y-2">
          <label htmlFor="receipt-note" className="text-sm">Catatan resi</label>
          <Textarea id="receipt-note" data-testid="receipt-note-input" value={value} onChange={event => setValue(event.target.value)} maxLength={1000} rows={4} placeholder="Contoh: Kirim sore, titip di depan toko." className="bg-ui-soft border-ui-line" />
        </div>}
        {action === 'whatsapp' && <div className="space-y-2">
          <label htmlFor="receipt-phone" className="text-sm">Nomor WhatsApp</label>
          <Input id="receipt-phone" data-testid="receipt-phone-input" type="tel" value={value} onChange={event => setValue(event.target.value)} placeholder="08… atau +62…" className="bg-ui-soft border-ui-line" />
          {value && !whatsappUrl && <p role="alert" className="text-sm text-danger-readable">Masukkan nomor WhatsApp yang valid.</p>}
        </div>}
        {action === 'whatsapp' && <pre data-testid="draft-receipt-preview" className="max-h-64 overflow-y-auto whitespace-pre rounded-md bg-ui-soft p-3 font-mono text-xs leading-relaxed">{message}</pre>}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
          {['discount', 'note'].includes(action) && <Button type="submit" data-testid="cart-action-save" disabled={discountInvalid} className="bg-brand-button hover:bg-brand-hover">Simpan</Button>}
          {action === 'whatsapp' && (whatsappUrl ? <Button asChild className="bg-brand-button hover:bg-brand-hover"><a data-testid="open-receipt-whatsapp" href={whatsappUrl} target="_blank" rel="noopener noreferrer">Buka WhatsApp</a></Button> : <Button type="button" disabled>Buka WhatsApp</Button>)}
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}
